import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { ApiError, type Contact, type CustomField, type FieldValue } from '../../lib/api';
import { Input, Select } from '../../components/ui';
import { planBlockOf, planBlockText } from '../billing/plan';

export function errorMessage(err: unknown): string {
  // A plan refusal (402, D-183) in the reader's language; the toast carries "Changer d'offre".
  const block = planBlockOf(err);
  if (block) return planBlockText(block, i18n.t).body;
  return err instanceof ApiError ? err.message : i18n.t('contacts.errorGeneric');
}

export function contactDisplayName(contact: Contact): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  return name || contact.username || contact.email || contact.phone || i18n.t('contacts.unnamed');
}

/** Channels a contact can come from; the mock one is the dev simulator. */
export const CHANNEL_CODES = ['instagram', 'messenger', 'mock'] as const;

export const channelLabel = (code: string): string =>
  code === 'instagram' || code === 'messenger' ? i18n.t(`channel.${code}`) : code === 'mock' ? i18n.t('contacts.mockChannel') : code;

export const STATUS_OPTIONS = ['active', 'unsubscribed', 'blocked'] as const;
type ContactStatus = (typeof STATUS_OPTIONS)[number];

/** Statuses as the UI says them, "Actif" rather than the raw key (A07-05). */
export const statusLabel = (status: string): string =>
  (STATUS_OPTIONS as readonly string[]).includes(status) ? i18n.t(`contacts.statuses.${status as ContactStatus}`) : status;

/**
 * One typed editor per custom field type. Values travel as the API's JSON
 * shapes: string for text-likes and dates, number, boolean, null for unset.
 */
export function FieldValueInput({
  field,
  value,
  onChange,
}: {
  field: CustomField;
  value: FieldValue | undefined;
  onChange: (value: FieldValue) => void;
}) {
  const { t } = useTranslation();
  switch (field.type) {
    case 'number':
      return (
        <Input
          type="number"
          step="any"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'boolean':
      return (
        <Select
          value={value === true ? 'true' : value === false ? 'false' : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')}
        >
          <option value="">{t('contacts.form.notSet')}</option>
          <option value="true">{t('contacts.form.yes')}</option>
          <option value="false">{t('contacts.form.no')}</option>
        </Select>
      );
    case 'date':
      return (
        <Input
          type="date"
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );
    case 'select':
      return (
        <Select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          <option value="">{t('contacts.form.notSet')}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      );
    default:
      return (
        <Input
          type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );
  }
}
